import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Generates message for tap targets section
 * @param {Array} failingTapTargets - The failing tap targets array
 * @returns {string|null} - Message for empty state or null if data exists
 */
const generateMessage = (failingTapTargets) => {
  if (!failingTapTargets || failingTapTargets?.length === 0) {
    return "Perfect, your page's tap targets are big enough and have enough space between them.";
  }
  return null;
};

/**
 * Builds tap targets table data from failing tap targets
 * @param {Array} failingTapTargets - The failing tap targets array from API
 * @returns {Array} - Array of table row objects
 */
const buildTapTargetsTable = (failingTapTargets) => {
  if (!failingTapTargets || failingTapTargets?.length === 0) {
    return [];
  }

  return failingTapTargets.map((target) => {
    const tapTarget = target?.snippet || '';
    const width = target?.domRect?.width ?? 0;
    const height = target?.domRect?.height ?? 0;
    const size = `${Math.round(width)}x${Math.round(height)}`;

    // Get first overlapping target from failingTapNeighbours
    const overlappingTarget = target?.failingTapNeighbours?.[0]?.snippet || '-';

    return {
      tapTarget,
      size,
      overlappingTarget,
    };
  });
};

/**
 * Transforms the mobile_taptargets criteria from WooRank API response
 * @param {Object} tapTargetsCriteria - The mobile_taptargets criteria object from the API
 * @returns {Object} - Transformed tap targets data for React components
 */
export const transformTapTargets = (tapTargetsCriteria) => {
  // Handle missing criteria - use 'neutral' status (maps to 'info' icon)
  if (!tapTargetsCriteria) {
    return {
      status: getStatus('neutral'),
      iconType: getIconType('neutral'),
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'No tap target data was found.',
      data: null,
    };
  }

  const data = tapTargetsCriteria?.data || {};
  const wooRankStatus = tapTargetsCriteria?.status || 'neutral';
  const importance = tapTargetsCriteria?.importance || 0;
  const solvability = tapTargetsCriteria?.solvability || 0;

  const failingTapTargets = data?.failingTapTargets || [];
  const hasFailingTargets = failingTapTargets?.length > 0;

  // Build table data from failing targets
  const tapTargetsTable = buildTapTargetsTable(failingTapTargets);

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty: false, // We always have some data if criteria exists
    hasFailingTargets,
    message: generateMessage(failingTapTargets),
    data: {
      tapTargetsTable,
      viewport: {
        isPresent: data?.viewport?.isPresent ?? false,
        isValid: data?.viewport?.isValid ?? false,
      },
    },
  };
};

export default transformTapTargets;

